import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  map,
  mergeMap,
  publishReplay,
  refCount,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { ModalComponent } from '../../core-components/modal/modal.component';
import { AtlasTranslateService } from '../../translate/translate.service';
import { BusinessNavLocationService } from './location.service';
import { BusinessNavLocationTabService } from './tab/tab.service';
import { ModalService } from '../../core-components/modal/modal.service';
import { Router } from '@angular/router';
import { BusinessNavDataService, LOCATIONS_PAGE_SIZE, LocationType } from '../../data.service';
import { UserViewType } from '../../atlas_sdk/enums';

const LIST_ITEM_HEIGHT = 72;

@Component({
  selector: 'atlas-location-switcher-modal',
  templateUrl: './location-switcher-modal.component.html',
  styleUrls: ['./location-switcher-modal.component.scss']
})
export class LocationSwitcherModalComponent implements OnInit, OnDestroy {
  @ViewChild('modal', { static: true }) modal: ModalComponent;
  @ViewChild('listContainer', { static: true }) listContainer: ElementRef;
  tabElemSelected$: Observable<ElementRef>;

  allTabText$: Observable<string>;
  accountGroupTabText$: Observable<string>;
  brandTabText$: Observable<string>;
  showTabs$: Observable<boolean>;

  userViewType$: Observable<UserViewType>;
  adminViewDefault: string = 'You are an admin, so you may see accounts not available to all users.';
  viewType = UserViewType;

  private scrollDebounceTime = Date.now();

  private unsubscribe$ = new Subject<null>();

  private needsMore$$ = new BehaviorSubject<boolean>(true);
  public needsMore$ = this.needsMore$$.asObservable();

  constructor(
    private tabService: BusinessNavLocationTabService,
    public locationService: BusinessNavLocationService,
    private t: AtlasTranslateService,
    private modalService: ModalService,
    private router: Router,
    private dataService: BusinessNavDataService
  ) {
    this.tabService.clear();
  }

  ngOnInit(): void {
    const allTabTranslatedText$ = this.t.stream('NAVIGATION.TABS.ALL', 'All');
    const accountGroupTabTranslatedText$ = this.t.stream(
      'NAVIGATION.TABS.LOCATIONS',
      'Locations'
    );
    const brandTabTranslatedText$ = this.t.stream(
      'NAVIGATION.TABS.BRANDS',
      'Brands'
    );

    this.userViewType$ = this.dataService.userViewType$;

    this.allTabText$ = combineLatest([
      allTabTranslatedText$,
      this.locationService.allListCount$,
      this.locationService.accountGroupListCount$
    ]).pipe(
      map(([text, count, ags]: [string, number, number]) => {
        if (ags === -1) {
          return text;
        } else {
          return `${text} (${count}+)`;
        }
      }),
      publishReplay(1),
      refCount()
    );
    this.accountGroupTabText$ = combineLatest([
      accountGroupTabTranslatedText$,
      this.locationService.accountGroupListCount$
    ]).pipe(
      map(([text, count]: [string, number]) => {
        if (count === -1) {
          return text;
        } else {
          return `${text} (${count}+)`;
        }
      }),
      publishReplay(1),
      refCount()
    );
    this.brandTabText$ = combineLatest([
      brandTabTranslatedText$,
      this.locationService.brandListCount$,
      this.locationService.accountGroupListCount$
    ]).pipe(
      map(([text, count, ags]: [string, number, number]) => {
        if (ags === -1) {
          return text;
        } else {
          return `${text} (${count}+)`;
        }
      }),
      publishReplay(1),
      refCount()
    );

    this.showTabs$ = of(true);

    this.tabElemSelected$ = this.tabService.selectedTabElement$;
    // Close if we've navigated to a different location
    this.router.events.pipe(take(1)).subscribe(() => this.modalService.close());

    // Reset scrolling on location list changes.
    this.locationService.allListCount$
      .pipe(
        tap(() => {
          this.listContainer.nativeElement.scrollTop = 0;
          setTimeout(() => this.listScroll(), 10);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.needsMore$.pipe(
      takeUntil(this.unsubscribe$),
      mergeMap((needsMore) => {
        if (needsMore) {
          return this.tabService.selectedTabIndex$.pipe(
            take(1),
            concatMap(tab => {
              if (tab === this.locationService.LOCATIONS_TAB_POSITION) {
                return this.loadMore(LocationType.BUSINESS);
              } else if (tab === this.locationService.BRANDS_TAB_POSITION) {
                return this.loadMore(LocationType.BRAND);
              } else {
                return forkJoin(this.loadMore(LocationType.BUSINESS), this.loadMore(LocationType.BRAND));
              }
            })
          );
        } else {
          return of(null);
        }
      })
    ).subscribe();
    this.tabService.selectedTabId$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged(),
      tap(() => this.listScroll())
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.locationService.setSearchTerm('');
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  listScroll(): void {
    if (
      this.checkScrollHeight()
    ) {
      // Rate limiting to once every 500ms is an arbitrarily chosen time frame. requests will auto chain to ensure that
      // more locations load if needed, so there is a diminished risk of the client not loading more requests with this
      // set a bit higher. We also only limit requests that will result in an observable, as the others will return harmlessly.
      const now = Date.now();
      if (now - this.scrollDebounceTime > 500) {
        this.scrollDebounceTime = now;
        this.needsMore$$.next(true);
      }
    }
  }

  textChanged(text: string): void {
    this.locationService.setSearchTerm(text);
    this.listScroll();
  }

  private loadMore(locationType: LocationType): Observable<any> {
    return this.dataService.loadMore(locationType).pipe(
      concatMap((resp) => {
        if (resp.hasMore) {
          if (resp.loading) {
            return resp.loading.pipe(map(() => true));
          } else {
            return of(true);
          }
        }
        return of(false);
      }),
      tap(hasMore => {
        if (hasMore) {
          this.needsMore$$.next(this.checkScrollHeight());
        }
      }),
    );
  }

  private checkScrollHeight(): boolean {
    const element: HTMLElement = this.listContainer?.nativeElement;
    if (!element) { return null; }
    return element.scrollTop >= element.scrollHeight - element.offsetHeight - LIST_ITEM_HEIGHT * LOCATIONS_PAGE_SIZE / 2;
  }
}
